import React, { useState, useEffect } from 'react';
import { getComponentTypes, getComponentsByCategory } from '../../../config/componentTypes';
import { fetchComponentLibrary } from '../../../services/firestoreService';
import { useAppStore } from '../../../store/useAppStore';

const ComponentSelector = ({ onAddComponent, onAddFormComponent, onAddCardComponent, onAddFromLibrary, refreshTrigger }) => {
  console.log('ComponentSelector: Props received:', { onAddComponent: !!onAddComponent, onAddFormComponent: !!onAddFormComponent, onAddCardComponent: !!onAddCardComponent, onAddFromLibrary: !!onAddFromLibrary, refreshTrigger });
  const [selectedType, setSelectedType] = useState('');
  const [savedComponents, setSavedComponents] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { appName } = useAppStore();
  const componentTypes = getComponentTypes();

  // Function to load saved components - can be called manually or automatically
  const loadSavedComponents = async () => {
    if (!appName) {
      console.log('ComponentSelector: No appName available');
      setLoadingSaved(false);
      return;
    }

    try {
      setLoadingSaved(true);
      console.log(`📚 [ComponentSelector] Fetching saved components for: ${appName}`);
      const components = await fetchComponentLibrary(appName);
      
      // Log complete component data to verify all fields are present
      console.group(`✅ [ComponentSelector] Loaded ${components?.length || 0} saved components`);
      components?.forEach((comp, idx) => {
        console.log(`Component ${idx + 1}:`, {
          id: comp.id,
          name: comp.name,
          type: comp.type,
          hasSpecificAttrs: !!comp.specificAttrs,
          hasCommonAttrs: !!comp.commonAttrs,
          hasFields: !!comp.specificAttrs?.fields,
          hasActions: !!comp.specificAttrs?.actions,
          fieldsCount: comp.specificAttrs?.fields?.length || 0,
          actionsCount: comp.specificAttrs?.actions?.length || 0
        });
      });
      console.groupEnd();
      
      setSavedComponents(components || []);
    } catch (error) {
      console.error('❌ [ComponentSelector] Failed to load saved components:', error);
      setSavedComponents([]);
    } finally {
      setLoadingSaved(false);
    }
  };

  // Manual refresh handler
  const handleManualRefresh = async () => {
    setRefreshing(true);
    await loadSavedComponents();
    setTimeout(() => setRefreshing(false), 500); // Show refresh animation
  };

  // Fetch saved components from appname_Components collection on mount and when refreshTrigger changes
  useEffect(() => {
    loadSavedComponents();
  }, [appName, refreshTrigger]); // Re-fetch when refreshTrigger changes (event-driven)

  // Group components by category
  const categories = {
    content: getComponentsByCategory('content'),
    interactive: getComponentsByCategory('interactive'),
    form: getComponentsByCategory('form'),
    layout: getComponentsByCategory('layout'),
    media: getComponentsByCategory('media'),
    data: getComponentsByCategory('data'),
    feedback: getComponentsByCategory('feedback'),
    navigation: getComponentsByCategory('navigation'),
    advanced: getComponentsByCategory('advanced')
  };

  const handleAdd = () => {
    if (selectedType) {
      console.log('ComponentSelector: Adding component type:', selectedType);
      
      // Check if it's a saved component (starts with "saved_")
      if (selectedType.startsWith('saved_')) {
        const componentId = selectedType.replace('saved_', '');
        const savedComponent = savedComponents.find(c => c.id === componentId);
        
        if (savedComponent && onAddFromLibrary) {
          console.log('ComponentSelector: Adding saved component from library:', savedComponent.name);
          onAddFromLibrary(savedComponent);
        } else {
          console.error('ComponentSelector: onAddFromLibrary is not defined or component not found!');
        }
      }
      // Special handling for Form component
      else if (selectedType === 'form') {
        console.log('ComponentSelector: Triggering form builder');
        if (onAddFormComponent) {
          onAddFormComponent();
        } else {
          console.error('ComponentSelector: onAddFormComponent is not defined!');
        }
      } 
      // Special handling for Card component (template picker)
      else if (selectedType === 'card') {
        console.log('ComponentSelector: Triggering card template picker');
        if (onAddCardComponent) {
          onAddCardComponent();
        } else {
          // Fallback to plain add if not wired
          onAddComponent?.('card');
        }
      }
      // Regular component
      else {
        console.log('ComponentSelector: Adding regular component');
        onAddComponent(selectedType);
      }
      setSelectedType('');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <select
        value={selectedType}
        onChange={(e) => setSelectedType(e.target.value)}
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: '14px',
          border: '1px solid #D1D5DB',
          borderRadius: '6px',
          backgroundColor: '#FFFFFF',
          cursor: 'pointer'
        }}
      >
        <option value="">
          {loadingSaved ? 'Loading components...' : `Select component... (${componentTypes.length + savedComponents.length} available)`}
        </option>
        
        {/* Saved Components Section (at the top) */}
        {!loadingSaved && savedComponents.length > 0 && (
          <optgroup label="💾 Saved Components (Reusable)">
            {savedComponents.map((component) => (
              <option key={`saved_${component.id}`} value={`saved_${component.id}`}>
                📦 {component.name || component.type} {component.description ? `- ${component.description}` : ''}
              </option>
            ))}
          </optgroup>
        )}

        {/* Separator between saved and new components */}
        {!loadingSaved && savedComponents.length > 0 && (
          <option disabled style={{ borderTop: '2px solid #E5E7EB', margin: '4px 0' }}>
            ──────────────────────────
          </option>
        )}
        
        {/* New Components Section */}
        {/* Content */}
        {categories.content.length > 0 && (
          <optgroup label="📝 Content">
            {categories.content.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </optgroup>
        )}

        {/* Interactive */}
        {categories.interactive.length > 0 && (
          <optgroup label="🖱️ Interactive">
            {categories.interactive.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </optgroup>
        )}

        {/* Form */}
        {categories.form.length > 0 && (
          <optgroup label="📋 Form">
            {categories.form.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </optgroup>
        )}

        {/* Layout */}
        {categories.layout.length > 0 && (
          <optgroup label="🎨 Layout">
            {categories.layout.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </optgroup>
        )}

        {/* Media */}
        {categories.media.length > 0 && (
          <optgroup label="🎬 Media">
            {categories.media.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </optgroup>
        )}

        {/* Data */}
        {categories.data.length > 0 && (
          <optgroup label="📊 Data">
            {categories.data.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </optgroup>
        )}

        {/* Feedback */}
        {categories.feedback.length > 0 && (
          <optgroup label="💬 Feedback">
            {categories.feedback.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </optgroup>
        )}

        {/* Navigation */}
        {categories.navigation.length > 0 && (
          <optgroup label="🧭 Navigation">
            {categories.navigation.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </optgroup>
        )}

        {/* Advanced */}
        {categories.advanced.length > 0 && (
          <optgroup label="⚙️ Advanced">
            {categories.advanced.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </optgroup>
        )}
      </select>
      
      {/* Buttons Row */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <button
          onClick={handleAdd}
          disabled={!selectedType}
          style={{
            flex: 1,
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: '600',
            color: '#FFFFFF',
            backgroundColor: selectedType ? '#3B82F6' : '#9CA3AF',
            border: 'none',
            borderRadius: '6px',
            cursor: selectedType ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s',
            whiteSpace: 'nowrap'
          }}
          onMouseOver={(e) => {
            if (selectedType) {
              e.target.style.backgroundColor = '#2563EB';
            }
          }}
          onMouseOut={(e) => {
            if (selectedType) {
              e.target.style.backgroundColor = '#3B82F6';
            }
          }}
        >
          Add
        </button>
        
        {/* Refresh Button */}
        <button
          onClick={handleManualRefresh}
          disabled={refreshing || loadingSaved}
          title="Refresh saved components"
          style={{
            padding: '8px 12px',
            fontSize: '16px',
            color: refreshing ? '#9CA3AF' : '#6B7280',
            backgroundColor: 'transparent',
            border: '1px solid #D1D5DB',
            borderRadius: '6px',
            cursor: refreshing || loadingSaved ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            flexShrink: 0,
            animation: refreshing ? 'spin 1s linear infinite' : 'none'
          }}
          onMouseOver={(e) => {
            if (!refreshing && !loadingSaved) {
              e.target.style.backgroundColor = '#F3F4F6';
              e.target.style.color = '#374151';
            }
          }}
          onMouseOut={(e) => {
            if (!refreshing && !loadingSaved) {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#6B7280';
            }
          }}
        >
          🔄
        </button>
      </div>
      
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ComponentSelector;
